"use client";

import { useMemo } from "react";
import { CommandGroup } from "@focal/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { CLIENT_ALL_BY_IDS_GET, getAllClients } from "@/api";
import { ConversationPersonTagSearchItem } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag/ConversationPersonTagSearchItem";
import type { Client, Participant, User, SuggestedParticipants } from "@/types";
import type { UpdateParticipantData } from "./ConversationPersonPopoverPaginated";
import { useUnclaimedSuggestions } from "./hooks";

interface SuggestedParticipantsSectionProps {
  suggestedParticipants?: SuggestedParticipants;
  heading?: string;
  participants?: Partial<Participant>[];
  users?: User[];
  participant?: Partial<Participant>;
  onSelectParticipant: (data: UpdateParticipantData) => void;
  onCloseModal: () => void;
  /** Cap on rendered suggestions, applied after unclaimed-filtering and
   * resolution — consuming one automatically promotes the next fallback.
   * Undefined = render all (conversation-details behavior). */
  maxVisible?: number;
}

export const SuggestedParticipantsSection = ({
  suggestedParticipants,
  heading = "Suggested",
  participants,
  users,
  participant,
  onSelectParticipant,
  onCloseModal,
  maxVisible,
}: SuggestedParticipantsSectionProps) => {
  const { unclaimedSuggestions, excludedClientIds } = useUnclaimedSuggestions(
    suggestedParticipants,
    participants,
  );

  const { data: suggestedClients } = useQuery({
    queryKey: [CLIENT_ALL_BY_IDS_GET, excludedClientIds],
    queryFn: () => getAllClients({ clientIds: excludedClientIds.join(",") }),
    enabled: excludedClientIds.length > 0,
  });

  const resolvedSuggestions = useMemo(() => {
    return unclaimedSuggestions
      .map((suggestion) => {
        const matchedUser = suggestion.userId
          ? users?.find((u) => u.id === suggestion.userId)
          : null;
        const matchedClient = suggestion.clientId
          ? suggestedClients?.find((c) => c.id === suggestion.clientId)
          : null;

        const matched = matchedUser || matchedClient;
        if (!matched) return null;

        return {
          id: suggestion.userId || suggestion.clientId,
          matched,
          type: (matchedUser ? "user" : "client") as "user" | "client",
        };
      })
      .filter(
        (
          s,
        ): s is {
          id: string | undefined;
          matched: User | Client;
          type: "user" | "client";
        } => s !== null,
      );
  }, [unclaimedSuggestions, users, suggestedClients]);

  const visibleSuggestions =
    maxVisible !== undefined
      ? resolvedSuggestions.slice(0, maxVisible)
      : resolvedSuggestions;

  if (visibleSuggestions.length === 0) return null;

  return (
    <>
      <CommandGroup heading={heading}>
        {visibleSuggestions.map((suggestion) => (
          <ConversationPersonTagSearchItem
            key={suggestion.id}
            name={`${suggestion.matched.firstname} ${suggestion.matched.lastname}`}
            email={suggestion.matched.email}
            type={suggestion.type}
            crmType={
              "crmContact" in suggestion.matched &&
              suggestion.matched.crmContact?.crmId
                ? suggestion.matched.crmContact.crmType
                : undefined
            }
            handleSelectAuthor={() => {
              onSelectParticipant({
                data: suggestion.matched,
                type: suggestion.type,
                participantId: participant?.id,
                speakerId: participant?.speakerId,
              });
              onCloseModal();
            }}
          />
        ))}
      </CommandGroup>
      <div className="-mx-1 h-px bg-border my-1" />
    </>
  );
};
